import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Clock, CalendarDays } from "lucide-react";
import { getGuardianId } from "@/lib/guardian";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

export const GuardianHistory = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/simulation/history/${getGuardianId()}`);
        setItems(res.ok ? await res.json() : []);
      } catch (e) {
        setItems([]);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="guardian-history">
      <button
        onClick={onBack}
        data-testid="history-back-btn"
        className="mb-8 inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/90"
      >
        <ArrowLeft size={16} />
        Back to profile
      </button>

      <div className="mb-2 flex items-center gap-2">
        <Clock size={13} style={{ color: ACCENT }} />
        <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">
          Private · This device only
        </span>
      </div>
      <h1 className="mb-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
        Guardian History
      </h1>
      <p className="mb-10 text-sm leading-relaxed text-white/50">
        A gentle record of the simulations completed on this device.
      </p>

      {loading ? (
        <div className="flex items-center gap-3 text-white/60" data-testid="history-loading">
          <Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} />
          Loading history…
        </div>
      ) : items.length === 0 ? (
        <p className="text-base text-white/55" data-testid="history-empty">
          No completed simulations yet. Once your child finishes a simulation, it will appear here.
        </p>
      ) : (
        <div className="space-y-4" data-testid="history-list">
          {items.map((it, i) => (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm"
              data-testid="history-item"
            >
              <div className="mb-3 flex items-center gap-2 text-xs text-white/45">
                <CalendarDays size={13} />
                {formatDate(it.created_at)}
              </div>
              <h3 className="mb-4 font-display text-lg font-medium text-white">{it.scenario}</h3>
              {it.capabilities.length > 0 && (
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">
                  Capabilities practised
                </p>
              )}
              {it.capabilities.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {it.capabilities.map((c, j) => (
                    <span
                      key={j}
                      className="rounded-full border px-3 py-1.5 text-xs text-white/80"
                      style={{ borderColor: "rgba(79,163,217,0.35)", background: "rgba(79,163,217,0.08)" }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default GuardianHistory;
